import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import MailRoundedIcon from '@mui/icons-material/MailRounded';
import LocalShippingRoundedIcon from '@mui/icons-material/LocalShippingRounded';
import TimerRoundedIcon from '@mui/icons-material/TimerRounded';
import MiniTaskGameHeader from '../components/MiniTaskGameHeader';
import { playMiniTaskSound } from '../soundEffects';
import { cargoSideFor, generateCargoSequence } from './cargoSequence';
import { CargoPackageVisual, CargoPanda } from './CargoVisuals';
import CargoUnlockOverlay from './CargoUnlockOverlay';
import CargoRuleChangeNotice from './CargoRuleChangeNotice';
import CargoGroupRuleCard from './CargoGroupRuleCard';

const COLOR_HEX = { blue: '#2563EB', yellow: '#FACC15', purple: '#9360F1', orange: '#FFAE2B' };
const COLOR_DARK = { blue: '#1747B8', yellow: '#D89A00', purple: '#5B29B8', orange: '#BD6800' };
const RULE_SETTLE_MS = 650;

function PandaWorker({ message }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
      <Box sx={{ flex: '0 0 auto', width: { xs: 49, sm: 59 }, height: { xs: 49, sm: 59 } }}>
        <CargoPanda size="100%" />
      </Box>
      <Box sx={{ position: 'relative', bgcolor: '#fff', border: '2px solid #C4B5FD', borderRadius: 99, px: { xs: 1.1, sm: 1.6 }, py: 0.7, boxShadow: '0 3px 0 rgba(91,33,182,.12)', '&::before': { content: '""', position: 'absolute', left: -7, top: '50%', width: 12, height: 12, bgcolor: '#fff', borderLeft: '2px solid #C4B5FD', borderBottom: '2px solid #C4B5FD', transform: 'translateY(-50%) rotate(45deg)' } }}>
        <Typography sx={{ position: 'relative', fontWeight: 1000, color: '#4C1D95', fontSize: { xs: 12, sm: 15 }, whiteSpace: 'nowrap' }}>{message}</Typography>
      </Box>
    </Box>
  );
}

function DirectionTarget({ side }) {
  return (
    <Box sx={{ minWidth: { xs: 48, sm: 62 }, height: { xs: 32, sm: 38 }, px: { xs: 0.7, sm: 1 }, borderRadius: 99, bgcolor: '#EEE9FF', border: '2px solid #A78BFA', color: '#4C1D95', display: 'grid', placeItems: 'center', boxShadow: '0 3px 0 rgba(76,29,149,.10)' }}>
      <Typography sx={{ fontWeight: 1000, fontSize: { xs: 10, sm: 13 }, lineHeight: 1 }}>{side === 'left' ? 'SOL' : 'SAĞ'}</Typography>
    </Box>
  );
}

function RuleRoute({ kind, value, side }) {
  const isColor = kind === 'color';
  const label = { blue: 'MAVİ', yellow: 'SARI', purple: 'MOR', orange: 'TURUNCU', box: 'KUTU', envelope: 'ZARF', tube: 'TÜP', bag: 'POŞET' }[value] || value;
  return (
    <Box sx={{ minWidth: 0, minHeight: { xs: 52, sm: 62 }, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 0.55, sm: 0.9 }, bgcolor: '#FBFAFF', border: '1.5px solid #E9E2FF', borderRadius: { xs: 2.5, sm: 3 }, px: { xs: 0.55, sm: 1 }, py: { xs: 0.55, sm: 0.7 } }}>
      <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: { xs: 0.4, sm: 0.65 } }}>
        <Box sx={{ flex: '0 0 auto', width: { xs: 34, sm: 42 }, height: { xs: 34, sm: 42 }, borderRadius: isColor ? '50%' : value === 'box' ? 1.4 : 2.5, bgcolor: isColor ? COLOR_HEX[value] : '#FFF4D6', color: '#8A4B12', display: 'grid', placeItems: 'center', border: `3px solid ${isColor ? COLOR_DARK[value] : '#E29A2A'}`, boxShadow: 'inset 0 -4px 0 rgba(15,23,42,.1)' }}>
          {!isColor && (value === 'box' ? <Inventory2RoundedIcon sx={{ fontSize: { xs: 21, sm: 26 } }} /> : value === 'envelope' ? <MailRoundedIcon sx={{ fontSize: { xs: 22, sm: 27 } }} /> : <CargoPackageVisual color="blue" packageType={value} size={31} />)}
        </Box>
        <Typography sx={{ minWidth: 0, fontWeight: 1000, color: '#6B4A16', fontSize: { xs: 9, sm: 12 }, lineHeight: 1 }}>{label}</Typography>
      </Box>
      <Typography aria-hidden sx={{ flex: '0 0 auto', fontSize: { xs: 18, sm: 23 }, fontWeight: 1000, color: '#7C3AED', lineHeight: 1 }}>→</Typography>
      <DirectionTarget side={side} />
    </Box>
  );
}

function RuleCard({ rule }) {
  const isColor = rule.rule_type === 'color';
  const values = Array.isArray(rule.values) && rule.values.length === 2
    ? rule.values
    : isColor ? ['blue', 'yellow'] : ['box', 'envelope'];
  const firstSide = cargoSideFor(rule, isColor ? { color: values[0] } : { package_type: values[0] });
  const secondSide = firstSide === 'left' ? 'right' : 'left';
  return (
    <Box sx={{ width: '100%', bgcolor: 'rgba(255,255,255,.96)', border: '2px solid #D8CCFF', borderRadius: { xs: 3.5, sm: 4.5 }, px: { xs: 0.8, sm: 1.3 }, pt: { xs: 0.75, sm: 0.95 }, pb: { xs: 0.85, sm: 1.1 }, boxShadow: '0 5px 0 rgba(91,33,182,.10)', position: 'relative', zIndex: 3 }}>
      <Typography sx={{ mb: { xs: 0.65, sm: 0.85 }, textAlign: 'center', color: '#6D28D9', fontWeight: 1000, fontSize: { xs: 11, sm: 14 }, lineHeight: 1.2, letterSpacing: '.025em' }}>{isColor ? 'RENGE GÖRE AYIR' : 'PAKET TÜRÜNE GÖRE AYIR'}</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', alignItems: 'stretch', gap: { xs: 0.7, sm: 1.2 } }}>
        <RuleRoute kind={isColor ? 'color' : 'type'} value={values[0]} side={firstSide} />
        <RuleRoute kind={isColor ? 'color' : 'type'} value={values[1]} side={secondSide} />
      </Box>
    </Box>
  );
}

function CargoPackage({ item, feedback, selectedSide, reducedMotion }) {
  if (!item) return <Box sx={{ height: 150 }} />;
  const reactionAnimation = feedback === 'correct'
    ? `cargo-sort-${selectedSide || 'left'} 250ms ease-in forwards`
    : feedback === 'wrong' ? 'cargo-reject 440ms ease-in-out'
    : feedback === 'missed' ? 'cargo-missed 300ms ease-in forwards' : 'none';
  return (
    <Box data-testid="cargo-package" sx={{ position: 'relative', width: { xs: 158, sm: 195, md: 215 }, height: { xs: 147, sm: 180, md: 195 }, animation: reducedMotion ? 'none' : reactionAnimation }}>
      <CargoPackageVisual color={item.color} packageType={item.package_type} size="100%" />
      {feedback && (
        <Box sx={{ position: 'absolute', right: -16, top: -10, width: 46, height: 46, borderRadius: '50%', bgcolor: feedback === 'correct' ? '#22C55E' : feedback === 'wrong' ? '#EF4444' : '#64748B', color: '#fff', border: '4px solid #fff', display: 'grid', placeItems: 'center', boxShadow: '0 7px 16px rgba(30,41,59,.2)' }}>
          {feedback === 'correct' ? <CheckRoundedIcon sx={{ fontSize: 30 }} /> : feedback === 'wrong' ? <CloseRoundedIcon sx={{ fontSize: 30 }} /> : <TimerRoundedIcon sx={{ fontSize: 28 }} />}
        </Box>
      )}
    </Box>
  );
}

function PackageDisplay({ item, feedback, selectedSide, reducedMotion }) {
  return (
    <Box
      key={item?.id}
      sx={{
        position: 'absolute',
        zIndex: 5,
        left: '50%',
        top: '54%',
        transform: 'translate(-50%, -50%)',
        animation: reducedMotion ? 'none' : 'cargo-appear 220ms cubic-bezier(.2,.9,.35,1.25)',
      }}
    >
      <CargoPackage item={item} feedback={feedback} selectedSide={selectedSide} reducedMotion={reducedMotion} />
    </Box>
  );
}

function DeliveryDock({ side, disabled, status, onClick }) {
  return (
    <ButtonBase
      data-testid={`delivery-dock-${side}`}
      aria-label={`${side === 'left' ? 'Sol' : 'Sağ'} teslimat alanı`}
      disabled={disabled}
      onClick={() => onClick(side)}
      sx={{
        minWidth: 0,
        minHeight: { xs: 104, sm: 126, md: 138 },
        flex: 1,
        maxWidth: 380,
        borderRadius: { xs: 3.5, sm: 4.5 },
        background: 'linear-gradient(180deg, #F8F6FF 0%, #EEEAF8 76%, #CEC5E5 77%)',
        border: { xs: '4px solid #8B78BE', sm: '5px solid #8B78BE' },
        color: '#4C1D95',
        boxShadow: status === 'correct' ? '0 0 0 6px #86EFAC, 0 7px 0 rgba(30,41,59,.16)' : status === 'wrong' ? '0 0 0 6px #FCA5A5, 0 7px 0 rgba(30,41,59,.16)' : '0 7px 0 rgba(30,41,59,.16)',
        transform: status === 'correct' ? 'translateY(3px)' : 'none',
        transition: 'background-color 220ms ease, box-shadow 150ms ease, transform 150ms ease',
        '&:disabled': { opacity: 1 },
      }}
    >
      <Box sx={{ position: 'absolute', top: { xs: 12, sm: 15 }, width: '66%', height: '52%', borderRadius: { xs: 2.5, sm: 3.2 }, bgcolor: '#FFFFFF', border: '3px solid #C4B5FD', display: 'grid', placeItems: 'center', color: '#5B21B6', boxShadow: 'inset 0 4px 10px rgba(91,33,182,.08)' }}>
        <LocalShippingRoundedIcon sx={{ fontSize: { xs: 34, sm: 44, md: 49 } }} />
      </Box>
      <Typography aria-hidden sx={{ position: 'absolute', bottom: { xs: 6, sm: 8 }, fontWeight: 1000, fontSize: { xs: 24, sm: 30 }, color: '#4C1D95', lineHeight: 1 }}>↓</Typography>
    </ButtonBase>
  );
}

export default function CargoSortingRoundView({ round, classTitle, soundEnabled, onToggleSound, onExit, onComplete }) {
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const durationMs = Number(round?.duration_seconds || 40) * 1000;
  const [phase, setPhase] = useState(round?.unlock_kind ? 'unlock' : 'rule-preview');
  const [segmentIndex, setSegmentIndex] = useState(0);
  const [itemIndex, setItemIndex] = useState(0);
  const [remainingMs, setRemainingMs] = useState(durationMs);
  const [counts, setCounts] = useState({ shown_count: 1, correct_count: 0, wrong_count: 0, missed_count: 0 });
  const [feedback, setFeedback] = useState(null);
  const [selectedSide, setSelectedSide] = useState(null);
  const [correctSide, setCorrectSide] = useState(null);
  const [locked, setLocked] = useState(false);
  const [exitOpen, setExitOpen] = useState(false);
  const [completeError, setCompleteError] = useState('');
  const countsRef = useRef(counts);
  const remainingRef = useRef(durationMs);
  const finishedRef = useRef(false);
  const phaseRef = useRef(phase);
  const startedAtRef = useRef(0);
  const startRemainingRef = useRef(durationMs);

  useEffect(() => { countsRef.current = counts; }, [counts]);
  useEffect(() => { phaseRef.current = phase; }, [phase]);

  useEffect(() => {
    if (phase !== 'rule-exit') return undefined;
    const timer = window.setTimeout(
      () => setPhase('playing'),
      reducedMotion ? 120 : RULE_SETTLE_MS,
    );
    return () => window.clearTimeout(timer);
  }, [phase, reducedMotion]);

  const segments = useMemo(() => {
    let previousItem = null;
    return (round?.rule_segments || []).map((rule, index) => {
      const items = generateCargoSequence({
        seed: `${round?.seed || 'cargo'}-${index}`,
        rule,
        count: 80,
        incompatiblePct: round?.incompatible_pct ?? 0,
        fixedColor: round?.fixed_color,
        fixedPackageType: round?.fixed_package_type,
        previousItem,
        configVersion: round?.config_version,
        colorPair: round?.color_pair,
        packageTypePair: round?.package_type_pair,
        colorValues: round?.color_values,
        packageTypeValues: round?.package_type_values,
        focusValue: round?.focus_value,
      });
      previousItem = items[items.length - 1];
      return { rule, items };
    });
  }, [round]);

  const activeSegment = segments[segmentIndex] || segments[0];
  const rule = useMemo(
    () => activeSegment?.rule || { rule_type: 'color', mapping: 'normal' },
    [activeSegment?.rule],
  );
  const currentItem = activeSegment?.items[itemIndex % (activeSegment?.items.length || 1)];

  const updateCounts = useCallback((key) => {
    setCounts((current) => {
      const next = { ...current, [key]: current[key] + 1 };
      countsRef.current = next;
      return next;
    });
  }, []);

  const finishRound = useCallback(async () => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    setLocked(true);
    setPhase('completing');
    setCompleteError('');
    try {
      await onComplete(countsRef.current);
    } catch (error) {
      finishedRef.current = false;
      setCompleteError(error?.message || 'Sonuç kaydedilemedi.');
      setPhase('error');
    }
  }, [onComplete]);

  useEffect(() => {
    if (phase !== 'playing') return undefined;
    startedAtRef.current = Date.now();
    startRemainingRef.current = remainingRef.current;
    const timer = window.setInterval(() => {
      const next = Math.max(0, startRemainingRef.current - (Date.now() - startedAtRef.current));
      remainingRef.current = next;
      setRemainingMs(next);
      if (next <= 0) {
        window.clearInterval(timer);
        finishRound();
      }
    }, 80);
    return () => window.clearInterval(timer);
  }, [finishRound, phase]);

  const showNextPackage = useCallback(() => {
    if (finishedRef.current || remainingRef.current <= 0) return;
    const switchAtMs = Number(round?.switch_at_seconds || 0) * 1000;
    const elapsedMs = durationMs - remainingRef.current;
    if (segments.length > 1 && segmentIndex === 0 && elapsedMs >= switchAtMs) {
      setPhase('rule-change');
      setSegmentIndex(1);
      setItemIndex(0);
      setFeedback(null);
      setSelectedSide(null);
      setCorrectSide(null);
      // V3 requires acknowledgment so an early reader can actually learn the
      // new rule. Historical V1/V2 rounds keep their original automatic timing.
      if (Number(round?.config_version) >= 3) return;
      window.setTimeout(() => {
        if (finishedRef.current) return;
        updateCounts('shown_count');
        setLocked(false);
        setPhase('playing');
      }, reducedMotion ? 700 : 1200);
      return;
    }
    setItemIndex((current) => current + 1);
    updateCounts('shown_count');
    setFeedback(null);
    setSelectedSide(null);
    setCorrectSide(null);
    setLocked(false);
  }, [durationMs, reducedMotion, round?.config_version, round?.switch_at_seconds, segmentIndex, segments.length, updateCounts]);

  const giveFeedback = useCallback((outcome, chosenSide) => {
    const answerSide = cargoSideFor(rule, currentItem);
    const delay = outcome === 'correct'
      ? Number(round?.correct_feedback_ms || 250)
      : outcome === 'wrong'
      ? Number(round?.wrong_feedback_ms || 500)
      : Number(round?.missed_feedback_ms || 300);
    setLocked(true);
    setFeedback(outcome);
    setSelectedSide(chosenSide || null);
    setCorrectSide(answerSide);
    updateCounts(outcome === 'correct' ? 'correct_count' : outcome === 'wrong' ? 'wrong_count' : 'missed_count');
    if (outcome !== 'missed') playMiniTaskSound(outcome, soundEnabled);
    window.setTimeout(showNextPackage, delay);
  }, [currentItem, round, rule, showNextPackage, soundEnabled, updateCounts]);

  useEffect(() => {
    if (phase !== 'playing' || locked || !currentItem) return undefined;
    const timeout = window.setTimeout(() => giveFeedback('missed'), Number(round?.response_ms || 3000));
    return () => window.clearTimeout(timeout);
  }, [currentItem, giveFeedback, locked, phase, round?.response_ms]);

  const handleBay = (side) => {
    if (phase !== 'playing' || locked || !currentItem) return;
    const answerSide = cargoSideFor(rule, currentItem);
    giveFeedback(side === answerSide ? 'correct' : 'wrong', side);
  };

  const bayStatus = (side) => {
    if (!feedback) return null;
    if (selectedSide === side && feedback === 'wrong') return 'wrong';
    if (feedback === 'correct' && selectedSide === side) return 'correct';
    const teachingRound = round?.fixed_color || round?.fixed_package_type || Number(round?.level || 1) <= 4;
    if (teachingRound && feedback === 'wrong' && correctSide === side) return 'correct';
    return null;
  };

  const ruleInstruction = rule.rule_type === 'color' ? 'Paketin rengine bak!' : 'Paketin türüne bak!';

  return (
    <Box sx={{
      minHeight: '100vh',
      bgcolor: '#FFF9EC',
      backgroundImage: 'radial-gradient(circle at 8% 22%, rgba(253,224,71,.16) 0 70px, transparent 71px), radial-gradient(circle at 92% 32%, rgba(167,139,250,.14) 0 90px, transparent 91px)',
      color: '#172554',
      overflow: 'hidden',
      '@keyframes cargo-appear': {
        from: { transform: 'translate(-50%, -50%) scale(.82)', opacity: 0 },
        to: { transform: 'translate(-50%, -50%) scale(1)', opacity: 1 },
      },
      '@keyframes cargo-sort-left': {
        '0%': { transform: 'translate(0, 0) rotate(0)', opacity: 1 },
        '100%': { transform: 'translate(-125%, clamp(230px, 38vh, 340px)) rotate(-12deg) scale(.62)', opacity: 0 },
      },
      '@keyframes cargo-sort-right': {
        '0%': { transform: 'translate(0, 0) rotate(0)', opacity: 1 },
        '100%': { transform: 'translate(125%, clamp(230px, 38vh, 340px)) rotate(12deg) scale(.62)', opacity: 0 },
      },
      '@keyframes cargo-reject': {
        '0%, 100%': { transform: 'translateX(0)' },
        '20%, 60%': { transform: 'translateX(-12px) rotate(-3deg)' },
        '40%, 80%': { transform: 'translateX(12px) rotate(3deg)' },
      },
      '@keyframes cargo-missed': {
        from: { transform: 'translateY(0)', opacity: 1 },
        to: { transform: 'translateY(75px) scale(.8)', opacity: 0 },
      },
      '@keyframes cargo-rule-focus': {
        '0%': { transform: 'translateY(clamp(145px, 24vh, 205px)) scale(.76)', opacity: 0 },
        '100%': { transform: 'translateY(clamp(145px, 24vh, 205px)) scale(1.04)', opacity: 1 },
      },
      '@keyframes cargo-rule-settle': {
        '0%': { transform: 'translateY(clamp(145px, 24vh, 205px)) scale(1.04)', opacity: 1 },
        '100%': { transform: 'translateY(0) scale(1)', opacity: 1 },
      },
      '@keyframes cargo-rule-backdrop-in': {
        '0%': { opacity: 0 },
        '100%': { opacity: 1 },
      },
      '@keyframes cargo-rule-backdrop-out': {
        '0%': { opacity: 1 },
        '100%': { opacity: 0 },
      },
    }}>
      {round.preview && <Box sx={{ position: 'fixed', bottom: 10, left: 10, zIndex: 20, bgcolor: '#FACC15', color: '#713F12', px: 1.4, py: 0.45, borderRadius: 99, fontWeight: 1000 }}>TEST MODU</Box>}
      <Box sx={{ mx: 'auto', maxWidth: 1100, px: { xs: 1.2, sm: 2, md: 3.2 }, pb: 2 }}>
        <MiniTaskGameHeader isEndless={Boolean(round?.is_endless)} classTitle={classTitle} soundEnabled={soundEnabled} onToggleSound={onToggleSound} />

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'auto 1fr auto', md: '220px 1fr 220px' }, alignItems: 'center', gap: { xs: 0.7, md: 2 }, mt: { xs: 0.8, md: 1.4 }, mb: { xs: 0.8, md: 1.2 } }}>
          <ButtonBase onClick={() => setExitOpen(true)} disabled={phase === 'completing'} sx={{ minWidth: { xs: 50, sm: 118, md: 190 }, minHeight: { xs: 48, md: 58 }, borderRadius: 3, border: '2px solid #D8B4FE', bgcolor: '#fff', color: '#33258A', px: { xs: 0.8, sm: 1.5 }, display: 'flex', gap: 0.5, boxShadow: '0 4px 0 rgba(91,33,182,.12)' }}>
            <ArrowBackRoundedIcon />
            <Typography sx={{ display: { xs: 'none', sm: 'block' }, fontWeight: 1000 }}>Ana sayfa</Typography>
          </ButtonBase>
          <Box sx={{ textAlign: 'center', minWidth: 0 }}>
            <Typography sx={{ fontWeight: 1000, fontSize: { xs: 21, sm: 30, md: 39 }, color: '#251777', lineHeight: 1, whiteSpace: 'nowrap' }}>Kargo Ayrıştırma</Typography>
            <Typography sx={{ mt: 0.35, fontWeight: 900, color: '#6D45DF', fontSize: { xs: 12, sm: 16, md: 18 }, lineHeight: 1 }}>{round?.is_endless ? 'Sonsuz Karma' : `Seviye ${round?.level || 1}`} · Hedef {round?.target_correct || 10}</Typography>
          </Box>
          <Box sx={{ justifySelf: 'end', minWidth: { xs: 82, sm: 112, md: 150 }, minHeight: { xs: 48, md: 58 }, borderRadius: 3, bgcolor: remainingMs <= 10000 ? '#FEE2E2' : '#fff', border: `2px solid ${remainingMs <= 10000 ? '#FCA5A5' : '#D8B4FE'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 0.25, sm: 0.6 }, boxShadow: '0 4px 0 rgba(91,33,182,.12)', color: remainingMs <= 10000 ? '#B91C1C' : '#4C1D95' }}>
            <TimerRoundedIcon sx={{ fontSize: { xs: 20, sm: 25 } }} />
            <Typography sx={{ fontWeight: 1000, fontSize: { xs: 17, sm: 21 } }}>{Math.ceil(remainingMs / 1000)} sn</Typography>
          </Box>
        </Box>

        <Box sx={{ position: 'relative', width: '100%', maxWidth: 980, height: { xs: 'calc(100dvh - 148px)', sm: 'calc(100dvh - 164px)', md: 'calc(100dvh - 178px)' }, minHeight: { xs: 520, sm: 580 }, maxHeight: 730, mx: 'auto', borderRadius: { xs: 4, sm: 5.5 }, border: '3px solid #C4B5FD', overflow: 'hidden', display: 'grid', gridTemplateRows: 'auto auto minmax(190px, 1fr) auto', background: 'linear-gradient(180deg, #E8F9FF 0%, #F4FCFF 64%, #FFF4CE 64%, #FFE6A3 100%)', boxShadow: '0 9px 0 rgba(91,33,182,.10), 0 20px 40px rgba(76,29,149,.12)' }}>
          <Box
            sx={{
              position: 'relative',
              zIndex: phase === 'rule-preview' || phase === 'rule-exit' ? 12 : 4,
              p: { xs: 1, sm: 1.4 },
              pb: { xs: 0.7, sm: 0.9 },
              transformOrigin: 'top center',
              transform: reducedMotion && phase === 'rule-preview'
                ? 'translateY(clamp(145px, 24vh, 205px)) scale(1.04)'
                : 'none',
              animation: !reducedMotion && phase === 'rule-preview'
                ? 'cargo-rule-focus 520ms cubic-bezier(.18,.88,.28,1.18) both'
                : !reducedMotion && phase === 'rule-exit'
                ? `cargo-rule-settle ${RULE_SETTLE_MS}ms cubic-bezier(.4,0,.2,1) both`
                : 'none',
            }}
          >
            {rule.groups ? <CargoGroupRuleCard rule={rule} /> : <RuleCard rule={rule} />}
          </Box>

          <Box sx={{ position: 'relative', zIndex: 4, display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: { xs: 0.6, sm: 1 }, px: { xs: 1, sm: 1.4 }, pb: { xs: 0.5, sm: 0.8 } }}>
            {[
              ['Doğru', counts.correct_count, '#16A34A', '#DCFCE7'],
              ['Yanlış', counts.wrong_count, '#DC2626', '#FEE2E2'],
              ['Kaçırılan', counts.missed_count, '#64748B', '#F1F5F9'],
            ].map(([label, value, color, background]) => (
              <Box key={label} sx={{ minWidth: 0, bgcolor: background, border: '2px solid #fff', borderRadius: 2.5, px: { xs: 0.5, sm: 1.2 }, py: { xs: 0.5, sm: 0.7 }, textAlign: 'center', boxShadow: '0 3px 0 rgba(71,46,134,.10)' }}>
                <Typography component="span" sx={{ fontWeight: 900, color: '#475569', fontSize: { xs: 10, sm: 13 } }}>{label} </Typography>
                <Typography component="span" sx={{ fontWeight: 1000, color, fontSize: { xs: 15, sm: 19 } }}>{value}</Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ position: 'relative', minHeight: 0, overflow: 'visible' }}>
            <Box aria-hidden sx={{ position: 'absolute', left: '7%', top: '25%', width: { xs: 35, sm: 48 }, height: { xs: 28, sm: 38 }, borderRadius: 2, bgcolor: '#FDA4AF', border: '3px solid rgba(190,24,93,.20)', transform: 'rotate(-9deg)', boxShadow: 'inset 0 -5px 0 rgba(190,24,93,.10)' }}>
              <Box sx={{ width: '20%', height: '100%', mx: 'auto', bgcolor: 'rgba(255,255,255,.50)' }} />
            </Box>
            <Box aria-hidden sx={{ position: 'absolute', right: '7%', top: '34%', width: { xs: 33, sm: 46 }, height: { xs: 26, sm: 36 }, borderRadius: 2, bgcolor: '#6EE7B7', border: '3px solid rgba(5,150,105,.20)', transform: 'rotate(10deg)', boxShadow: 'inset 0 -5px 0 rgba(5,150,105,.10)' }}>
              <Box sx={{ width: '20%', height: '100%', mx: 'auto', bgcolor: 'rgba(255,255,255,.50)' }} />
            </Box>
            <Box aria-hidden sx={{ position: 'absolute', left: '11%', bottom: '17%', width: 16, height: 16, borderRadius: '50%', bgcolor: '#FBBF24', boxShadow: '26px 11px 0 #A78BFA' }} />
            <Box aria-hidden sx={{ position: 'absolute', right: '12%', bottom: '16%', width: 14, height: 14, borderRadius: '50%', bgcolor: '#60A5FA', boxShadow: '-25px 10px 0 #FB7185' }} />

            <Box sx={{ position: 'absolute', zIndex: 4, top: { xs: 7, sm: 12 }, left: '50%', transform: 'translateX(-50%)' }}>
              <PandaWorker message={ruleInstruction} />
            </Box>

            <Box aria-hidden sx={{ position: 'absolute', zIndex: 2, left: '50%', top: '54%', width: { xs: 255, sm: 340 }, height: { xs: 205, sm: 265 }, transform: 'translate(-50%, -50%)', borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,.98) 0 45%, rgba(255,255,255,.58) 46% 63%, rgba(255,255,255,0) 64%)' }} />
            <Box aria-hidden sx={{ position: 'absolute', zIndex: 2, left: '50%', top: '72%', width: { xs: 170, sm: 220 }, height: { xs: 22, sm: 29 }, transform: 'translateX(-50%)', borderRadius: '50%', bgcolor: 'rgba(76,29,149,.14)', filter: 'blur(2px)' }} />

            {phase === 'playing' ? (
              <PackageDisplay key={currentItem?.id} item={currentItem} feedback={feedback} selectedSide={selectedSide} reducedMotion={reducedMotion} />
            ) : null}
          </Box>

          <Box sx={{ position: 'relative', zIndex: 6, px: { xs: 1, sm: 2 }, pt: { xs: 0.8, sm: 1 }, pb: { xs: 1.3, sm: 1.7 }, display: 'flex', alignItems: 'stretch', justifyContent: 'center', gap: { xs: 1.2, sm: 2.5 }, bgcolor: 'rgba(255,255,255,.52)', borderTop: '2px solid rgba(255,255,255,.82)' }}>
            <DeliveryDock side="left" disabled={phase !== 'playing' || locked} status={bayStatus('left')} onClick={handleBay} />
            <DeliveryDock side="right" disabled={phase !== 'playing' || locked} status={bayStatus('right')} onClick={handleBay} />
          </Box>

          {(phase === 'rule-preview' || phase === 'rule-exit') && (
            <Box
              data-testid="cargo-rule-preview"
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 11,
                bgcolor: 'rgba(37,23,119,.76)',
                backgroundImage: 'radial-gradient(circle at 50% 48%, rgba(124,58,237,.15), transparent 48%)',
                backdropFilter: 'blur(3px)',
                animation: reducedMotion
                  ? 'none'
                  : phase === 'rule-preview'
                  ? 'cargo-rule-backdrop-in 260ms ease-out both'
                  : `cargo-rule-backdrop-out ${RULE_SETTLE_MS}ms ease-in both`,
                pointerEvents: 'auto',
              }}
            >
              <Box
                role="status"
                aria-live="polite"
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: { xs: 3.2, sm: 4.2 },
                  px: 2,
                  textAlign: 'center',
                }}
              >
                <Box>
                  <Typography sx={{ color: '#FDE68A', fontWeight: 1000, fontSize: { xs: 13, sm: 16 }, letterSpacing: '.14em', lineHeight: 1 }}>
                    KURALA DİKKAT
                  </Typography>
                  <Typography sx={{ mt: 0.7, color: '#fff', fontWeight: 1000, fontSize: { xs: 25, sm: 34 }, lineHeight: 1.05, textShadow: '0 3px 0 rgba(0,0,0,.16)' }}>
                    Kuralı incele!
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  size="large"
                  disabled={phase === 'rule-exit'}
                  onClick={() => setPhase('rule-exit')}
                  sx={{
                    minWidth: { xs: 190, sm: 230 },
                    minHeight: { xs: 58, sm: 66 },
                    borderRadius: 99,
                    border: '3px solid #E9D5FF',
                    bgcolor: '#7C3AED',
                    color: '#fff',
                    fontWeight: 1000,
                    fontSize: { xs: 20, sm: 24 },
                    textTransform: 'none',
                    boxShadow: '0 8px 0 #4C1D95, 0 14px 26px rgba(15,23,42,.24)',
                    transition: 'transform 140ms ease, box-shadow 140ms ease, background-color 140ms ease',
                    '&:hover': { bgcolor: '#6D28D9', transform: 'translateY(-2px)', boxShadow: '0 10px 0 #4C1D95, 0 16px 28px rgba(15,23,42,.25)' },
                    '&:active': { transform: 'translateY(5px)', boxShadow: '0 3px 0 #4C1D95, 0 8px 18px rgba(15,23,42,.22)' },
                    '&.Mui-disabled': { bgcolor: '#8B5CF6', color: '#fff', opacity: 1, boxShadow: '0 4px 0 #4C1D95' },
                  }}
                >
                  {phase === 'rule-preview' ? 'Hazırım!' : 'Başlıyoruz!'}
                </Button>
              </Box>
            </Box>
          )}

          {phase === 'rule-change' && (
            <Box sx={{ position: 'absolute', inset: 0, zIndex: 10, bgcolor: 'rgba(245,240,255,.94)', display: 'grid', placeItems: 'center', px: { xs: 1, sm: 2 } }}>
              <CargoRuleChangeNotice
                beforeRule={segments[0]?.rule || rule}
                afterRule={rule}
                manual={Number(round?.config_version) >= 3}
                onContinue={() => {
                  if (finishedRef.current) return;
                  updateCounts('shown_count');
                  setLocked(false);
                  setPhase('playing');
                }}
              />
            </Box>
          )}

          {phase === 'unlock' && (
            <CargoUnlockOverlay kind={round?.unlock_kind} onContinue={() => setPhase('rule-preview')} />
          )}

          {(phase === 'completing' || phase === 'error') && (
            <Box sx={{ position: 'absolute', inset: 0, zIndex: 11, bgcolor: 'rgba(255,255,255,.94)', display: 'grid', placeItems: 'center', px: 2 }}>
              <Box sx={{ textAlign: 'center', maxWidth: 420 }}>
                {phase === 'completing' ? <CircularProgress size={62} sx={{ color: '#7C3AED' }} /> : <CloseRoundedIcon sx={{ color: '#EF4444', fontSize: 72 }} />}
                <Typography sx={{ mt: 1.5, color: '#251777', fontWeight: 1000, fontSize: { xs: 27, sm: 38 } }}>{phase === 'completing' ? 'Harika çalıştın!' : 'Sonuç kaydedilemedi'}</Typography>
                <Typography sx={{ color: '#6D5D93', fontWeight: 850 }}>{phase === 'completing' ? 'Sonucun kaydediliyor...' : completeError}</Typography>
                {phase === 'error' && <Button onClick={finishRound} variant="contained" sx={{ mt: 2, borderRadius: 99, fontWeight: 1000 }}>Tekrar dene</Button>}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      <Dialog open={exitOpen} onClose={() => setExitOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 4, border: '2px solid #E9D5FF' } }}>
        <DialogTitle sx={{ color: '#251777', fontWeight: 1000 }}>Görevden çıkmak istiyor musun?</DialogTitle>
        <DialogContent><Typography sx={{ color: '#6D5D93', fontWeight: 800 }}>Bu turdaki ilerlemen kaydedilmeyecek.</Typography></DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setExitOpen(false)} autoFocus sx={{ fontWeight: 900 }}>Göreve dön</Button>
          <Button color="error" variant="contained" onClick={onExit} sx={{ borderRadius: 99, fontWeight: 900 }}>Çık</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
